import React, { Fragment, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import {
  Button,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import { delay } from "utils/tools";
import SaveIcon from "@material-ui/icons/Save";
import TableResultConfirmAdjustmentPrices from "./TableResultConfirmAdjustmentPrices";

interface IBulkApproveProps {
  filteredData: ISubcontractorAdjustmentPrice[];
  basePath: string;
  filterValues: any;
  label: string;
}

interface IApproveResults {
  id: number;
  routeName: string;
  status: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const BulkActionApproveButton = ({ filteredData }: IBulkApproveProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IApproveResults[]>([]);
  const [processing, setProcessing] = useState(false);

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  const selectedAdjustmentPrices = filteredData.map((data) => ({
    id: data.id,
    routeName: data.routeName,
    status: data.status,
  }));

  const selectedRouteNames = filteredData.map((data) => data.routeName);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorAdjustmentPrices");
      refresh();
    }

    setOpen(false);
  };

  const handleApprove = (
    selectedAdjustmentPrice: IApproveResults,
    index: number,
  ) => {
    provider
      .customUpdate(
        `subcontractorAdjustmentPrices/${selectedAdjustmentPrice.id}/approve`,
        {
          data: {},
        },
      )
      .then(() => {
        let tempAdjustments = selectedAdjustmentPrices;
        tempAdjustments[index].status = "Approved";
        setResults([...tempAdjustments]);
      })
      .catch((err: any) => {
        let tempAdjustments = selectedAdjustmentPrices;
        tempAdjustments[index].status = `Error, ${err.message}`;
        setResults([...tempAdjustments]);
      });
  };

  const handleConfirm = async (selectedAdjustmentPrices: IApproveResults[]) => {
    setProcessing(true);

    selectedAdjustmentPrices.map((selectedId) => {
      selectedId.status = "Loading...";
    });

    for (const selectedAdjustmentPrice of selectedAdjustmentPrices) {
      const index = selectedAdjustmentPrices.indexOf(selectedAdjustmentPrice);

      handleApprove(selectedAdjustmentPrice, index);
      await delay(2000);
    }

    setFinished(true);
    setProcessing(false);
  };

  return (
    <Fragment>
      <Button label="Approve Selected" onClick={handleOpen} />
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Approve Selected Adjustment Prices
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmAdjustmentPrices records={results} />
          ) : (
            <DialogContentText color="initial">
              Are you sure you want to approve this selected adjustment price
              for routes: {selectedRouteNames.join(", ")}?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={() => handleConfirm(selectedAdjustmentPrices)}
              color="primary"
              label="Confirm"
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default BulkActionApproveButton;
